import { toImmutable } from 'nuclear-js';

import _Reactor from 'data/reactor';
import _DataPopulation from 'data/data-population';
import _PartnerGetters from 'data/partner/getters';
import _StudentGetters from 'data/student/getters';
import _UserGetters from 'data/user/getters';
import _StudentActions from 'data/student/actions';
import _SessionStorageUtils from 'data/session-storage/utils';

export const convertGoalToPayload = function(goal, courseSpecificBid, educatorBid) {
    return goal.withMutations((g) => {
        g.remove('endTermName');
        g.remove('startTermName');
        g.remove('startTermGrade');
        g.remove('endTermGrade');
        g.set('subject', courseSpecificBid);
        g.set('educatorBid', educatorBid);

        // the endpoint will choke if these are sent, but not populated
        if (!g.get('typicalScore')) {
            g.remove('typicalScore');
        }
        if (!g.get('endTermScore')) {
            g.remove('endTermScore');
        }
        if (!g.get('startTermScore')) {
            g.remove('startTermScore');
        }
        if (!g.get('predictedGrowth')) {
            g.remove('predictedGrowth');
        }
        if (!g.get('startTermNumber')) {
            g.remove('startTermNumber');
        }
        if (!g.get('startTermWeeksOfInstruction')) {
            g.remove('startTermWeeksOfInstruction');
        }
    });
};

export const DataUtils = (
    reactor = _Reactor,
    dataPopulation = _DataPopulation,
    partnerGetters = _PartnerGetters,
    userGetters = _UserGetters,
    studentGetters = _StudentGetters,
    studentActions = _StudentActions,
    sessionStorageUtils = _SessionStorageUtils
) => {
    return {
        createSuccessfulGoalPersistedCallback: function(studentBid, courseSpecificBid, onSuccess, studentAction) {
            // handle all the ancillary junk that needs to happen, such as
            //      adding the goal to the student store and (maybe) updating
            //      session storage
            return (goal) => {
                onSuccess(toImmutable(goal));
                reactor.observe(
                    studentGetters.growthGoalsForStudentBidAndSubjectBid(studentBid, courseSpecificBid),
                    (updatedGoals) => {
                        sessionStorageUtils.saveStudentData(studentBid);
                    }
                );
                studentAction(studentBid, goal);
            };
        },

        saveGrowthGoal: function(studentBid, courseSpecificBid, goal, onSuccess, onFailure) {
            const partnerBid = reactor.evaluate(partnerGetters.partnerBid);
            if (!partnerBid) {
                throw new Error(`cannot save growth goal without partnerBid`);
            }

            const educatorBid = reactor.evaluate(userGetters.educatorBid);
            if (!educatorBid) {
                throw new Error(`cannot save growth goal without educatorBid`);
            }

            const payload = convertGoalToPayload(goal, courseSpecificBid, educatorBid);
            const successCallback = this.createSuccessfulGoalPersistedCallback(
                studentBid,
                courseSpecificBid,
                onSuccess,
                studentActions.addOrUpdateGrowthGoal
            );

            dataPopulation.postGrowthGoal(partnerBid, studentBid, payload).then((result) => {
                if (result.success) {
                    successCallback(result.goal);
                } else {
                    onFailure();
                }
            }); // catch not needed
        },
        updateGrowthGoal: function(studentBid, goal, goals, courseSpecificBid, onSuccess, onFailure) {
            const educatorBid = reactor.evaluate(userGetters.educatorBid);
            if (!educatorBid) {
                throw new Error(`cannot save growth goal withough educatorBid`);
            }

            const goalToUpdate = goals.find((g) => g.get('endTermNumber') === goal.get('endTermNumber'));
            const growthGoalBid = goalToUpdate ? goalToUpdate.get('growthGoalBid') : null;
            if (!growthGoalBid) {
                throw new Error(`could not find growthGoalBid for ${goal}`);
            }

            const payload = convertGoalToPayload(goal, courseSpecificBid, educatorBid);
            const successCallback = this.createSuccessfulGoalPersistedCallback(
                studentBid,
                courseSpecificBid,
                onSuccess,
                studentActions.addOrUpdateGrowthGoal
            );

            dataPopulation.putGrowthGoal(payload, growthGoalBid).then((result) => {
                if (result.success) {
                    successCallback(result.goal);
                } else {
                    onFailure();
                }
            }); // catch tain't needed here
        },
        deleteGrowthGoal: function(studentBid, courseSpecificBid, goal, onSuccess, onFailure) {
            const result = dataPopulation.deleteGrowthGoal(goal.get('growthGoalBid'));
            const successCallback = this.createSuccessfulGoalPersistedCallback(
                studentBid,
                courseSpecificBid,
                onSuccess,
                studentActions.deleteGrowthGoal
            );
            if (result) {
                successCallback(goal);
            } else {
                console.error('dataAccessUtils: deleteGrowthGoal onFailure');
                onFailure();
            }
        },
    };
};

export default DataUtils();
